import { ref, computed, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { usePointsStore } from '@/store/points';
import { useAppStore } from '@/store/app';
import { notify } from 'notiwind';
import dayjs from 'dayjs';
import { handleError } from '@/utils/error-handler';

/**
 * The balance date the user is asserting, in their OWN calendar day.
 *
 * `new Date()` serializes to a UTC instant, so a user west of UTC submitting late
 * in the evening sent tomorrow's date. That put the program's first entry a day
 * ahead of every correction the picker defaulted to, and `latestChange` ranks by
 * `MAX(balance_date)` — so the corrections were saved and never shown.
 * Matches flatpickr's `M d, Y` display format in StepBalance. See SUPPORT-781.
 */
function localBalanceDate() {
    return dayjs().format('MMM D, YYYY');
}

export function useAddRewardWizard() {
    const pointsStore = usePointsStore();

    const {
        programs,
        rewards,
        user,
        showProgramModal,
    } = storeToRefs(pointsStore);

    const {
        getRewards,
        setProgramModal,
    } = pointsStore;

    // State
    const step = ref(0);
    const errors = ref([]);
    const saveProgramBtnAction = ref(false);
    const programSearchQuery = ref('');
    const showUserDropdown = ref(false);
    const dropdownTop = ref(0);
    const dropdownLeft = ref(0);
    const dropdownWidth = ref(0);
    const tooltipVisible = ref(false);
    const tooltipStyle = ref({});
    const updateMethod = ref('manual');

    const program = ref({
        user: null,
        type: null,
        has_elite_status: null,
        elite_status: null,
        points: null,
        program_id: null,
        balance_date: localBalanceDate(),
    });

    const programTypes = [
        { type: 'airline', label: 'Airline', icon: 'airline', active: true },
        { type: 'hotel', label: 'Hotel', icon: 'hotel', active: true },
        { type: 'rail', label: 'Rail', icon: 'rail', active: true },
        { type: 'flexible_currency', label: 'Flexible Currency', icon: 'flexible_currency', active: true },
    ];

    // Computed
    const cdnUrl = computed(() => window.cdnUrl);

    const filteredProgramTypes = computed(() => programTypes);

    const chosenProgramType = computed(() => {
        return programTypes.find((obj) => obj.type === program.value.type) || null;
    });

    const chosenProgram = computed(() => {
        return programs.value.find((obj) => obj.id === parseInt(program.value.program_id)) || null;
    });

    const userPrograms = computed(() => {
        const chosenUser = program.value.user;
        if (chosenUser && chosenUser.programs) {
            return chosenUser.programs.map((p) => p.id);
        }
        return [];
    });

    const programChoices = computed(() => {
        programs.value.forEach((item) => {
            item.active = !(
                item.type === program.value.type &&
                userPrograms.value.includes(item.id)
            );
        });

        return programs.value.filter((obj) => obj.type === program.value.type);
    });

    const canChooseProgramChoice = computed(() => {
        return programChoices.value.some((item) => item.active);
    });

    const filteredProgramChoices = computed(() => {
        let choices = programChoices.value;

        if (programSearchQuery.value.trim() !== '') {
            const searchTerm = programSearchQuery.value.toLowerCase();
            choices = choices.filter((p) =>
                p.name.toLowerCase().includes(searchTerm)
            );
        }

        return choices;
    });

    const milesPoints = computed(() => {
        if (chosenProgram.value && chosenProgram.value.type === 'airline') {
            return 'Miles';
        }
        return 'Points';
    });

    const canSaveStep = computed(() => {
        return step.value === 3 && updateMethod.value === 'manual';
    });

    const canNextStep = computed(() => {
        return step.value < 3;
    });

    const totalSteps = computed(() => 4);

    const stepSubtitle = computed(() => {
        if (step.value === 0) {
            return rewards.value.users.length > 1
                ? 'Select user and program type'
                : 'Select program type';
        }
        if (step.value === 1) {
            const typeLabels = { airline: 'airline', hotel: 'hotel', rail: 'rail', flexible_currency: 'flexible currency' };
            const label = typeLabels[program.value.type];
            return label ? `Select ${label} program` : null;
        }
        if (step.value === 2) return 'Select option to update points';
        if (step.value === 3) return 'Enter program balance';
        return null;
    });

    const formattedPoints = computed({
        get() {
            if (!program.value.points) return '';
            return program.value.points
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        },
        set(value) {
            const numericValue = value.replace(/[^\d]/g, '');
            program.value.points = numericValue || null;
        },
    });

    // Methods
    function getProgramImageUrl(image) {
        if (!image) return '';
        const cleanImage = image.replace(/^programs?_images\//, '');
        return `https://my10x.nyc3.cdn.digitaloceanspaces.com/uploads/program_images/_small/${cleanImage}`;
    }

    function resetProgram() {
        step.value = 0;
        programSearchQuery.value = '';
        program.value = {
            user:
                rewards.value.users.length === 1
                    ? rewards.value.users[0]
                    : null,
            type: null,
            has_elite_status: null,
            elite_status: null,
            points: null,
            program_id: null,
            balance_date: localBalanceDate(),
        };
        updateMethod.value = 'manual';
    }

    function nextStep() {
        if (step.value === 0) {
            if (
                rewards.value.users.length > 1 &&
                (!program.value.user || !program.value.user.id)
            ) {
                errors.value = [{ message: 'Please choose a user.' }];
                return;
            }
            if (!program.value.type) {
                errors.value = [{ message: 'Please select a program type.' }];
                return;
            }
            errors.value = [];
            step.value = 1;
            return;
        }

        if (step.value === 1) {
            if (!program.value.program_id) {
                errors.value = [{ message: 'Please select a program.' }];
                return;
            }
            errors.value = [];
            step.value = 2;
            return;
        }

        if (step.value === 2) {
            if (!updateMethod.value) {
                errors.value = [{ message: 'Please select an update method.' }];
                return;
            }
            errors.value = [];
            step.value = 3;
            return;
        }

        if (step.value === 3 && !program.value.points) {
            errors.value = [{ message: 'Please enter your points.' }];
            return;
        }

        errors.value = [];
        step.value++;
    }

    function prevStep() {
        step.value--;
    }

    function validateStep() {
        errors.value = [];
        switch (step.value) {
            case 0:
                if (!program.value.user) {
                    errors.value.push({ message: 'Please choose a user.' });
                }
                break;
            case 1:
                if (!program.value.type) {
                    errors.value.push({ message: 'Please choose a program type.' });
                }
                break;
            case 2:
                if (!program.value.program_id) {
                    errors.value.push({ message: 'Please choose a program.' });
                }
                break;
            case 3:
                if (!program.value.points) {
                    errors.value.push({ message: 'Please enter your points.' });
                }
                break;
            case 4:
                if (program.value.type !== 'flexible_currency') {
                    if (
                        program.value.has_elite_status &&
                        program.value.has_elite_status === 'Y' &&
                        !program.value.elite_status
                    ) {
                        errors.value.push({ message: 'Please enter your elite status.' });
                    }
                }
                break;
        }
    }

    function addReward() {
        validateStep();

        if (errors.value.length === 0) {
            saveProgramBtnAction.value = true;
            let data = Object.assign({}, program.value);

            if (program.value.user && program.value.user.id) {
                data.user = {
                    id: program.value.user.id,
                    name: program.value.user.name || '',
                };
            } else {
                data.user = null;
            }

            ApiService.apiCall('/rewards', 'post', data)
                .then((response) => {
                    setTimeout(() => {
                        saveProgramBtnAction.value = false;
                        step.value = 5;
                    }, 500);

                    if (import.meta.env.VITE_APP_ENV === 'production') {
                        woopra.track('program-added-my10x', {
                            program: chosenProgram.value.name,
                            balance: program.value.points,
                            uuid: localStorage.getItem('uuid'),
                        });
                    }

                    if (window._hsq) {
                        const appStore = useAppStore();
                        const loggedInUser = appStore.userSettings;
                        let userFirstName = null;
                        let userLastName = null;

                        if (loggedInUser && loggedInUser.name) {
                            const nameParts = loggedInUser.name.split(' ');
                            userFirstName = nameParts[0];
                            if (nameParts.length > 1) {
                                userLastName = nameParts.slice(1).join(' ');
                            }
                        }

                        const eventProperties = {
                            email: loggedInUser ? loggedInUser.email : null,
                            firstname: userFirstName,
                            lastname: userLastName,
                            my10x_user_uuid: loggedInUser ? loggedInUser.uuid : null,
                            program_id: program.value.program_id,
                            program_name: chosenProgram.value ? chosenProgram.value.name : null,
                            program_type: program.value.type,
                            program_balance: program.value.points,
                            program_owner_user_id: program.value.user ? program.value.user.id : null,
                        };

                        for (const key in eventProperties) {
                            if (eventProperties[key] === null || eventProperties[key] === undefined) {
                                delete eventProperties[key];
                            }
                        }

                        window._hsq.push([
                            'trackCustomBehavioralEvent',
                            {
                                name: 'pe22692776_completed_add_points_program',
                                properties: eventProperties,
                            },
                        ]);
                    }

                    let rewardsMessage = response.data.has_rewards
                        ? 'Your rewards will sync automatically each night.'
                        : 'You will need to manually enter your rewards balance.';

                    notify({
                        group: 'success',
                        title: 'Success',
                        text: 'Your program has been created. ' + rewardsMessage,
                    });
                })
                .catch((error) => {
                    handleError(error);
                    saveProgramBtnAction.value = false;
                });
        }
    }

    function addAnotherReward() {
        resetProgram();
    }

    function closeModal() {
        resetProgram();
        errors.value = [];
        showUserDropdown.value = false;
        setProgramModal(false);
        getRewards();
    }

    function toggleUserDropdown(btnEl) {
        showUserDropdown.value = !showUserDropdown.value;
        if (showUserDropdown.value && btnEl) {
            const dropdownRect = btnEl.getBoundingClientRect();
            // The dropdown is `position: fixed`, so coordinates must be
            // viewport-relative — do NOT add window.scrollX/scrollY.
            const viewportHeight = window.innerHeight;
            const dropdownMaxHeight = 250;
            const spaceBelow = viewportHeight - dropdownRect.bottom;
            const flipAbove =
                spaceBelow < dropdownMaxHeight &&
                dropdownRect.top > spaceBelow;

            dropdownTop.value = flipAbove
                ? Math.max(dropdownRect.top - dropdownMaxHeight - 5, 5)
                : dropdownRect.bottom + 5;
            dropdownLeft.value = dropdownRect.left;
            dropdownWidth.value = dropdownRect.width;
            nextTick(() => {
                const firstOption = document.querySelector(
                    '[role="listbox"] button[role="option"]'
                );
                if (firstOption) firstOption.focus();
            });
        }
    }

    function selectUser(selectedUser) {
        program.value.user = selectedUser;
        showUserDropdown.value = false;
        errors.value = errors.value.filter(
            (error) => error.message !== 'Please choose a user.'
        );
    }

    function closeUserDropdown() {
        showUserDropdown.value = false;
    }

    function clearError() {
        errors.value = [];
    }

    function showTooltip(event) {
        tooltipVisible.value = true;
        const target = event.target;
        const rect = target.getBoundingClientRect();
        tooltipStyle.value = {
            top: `${rect.top - 40}px`,
            left: `${rect.left + rect.width / 2}px`,
            transform: 'translateX(-50%)',
        };
    }

    function hideTooltip() {
        tooltipVisible.value = false;
    }

    // Watchers
    watch(showProgramModal, (value) => {
        if (value) {
            step.value = 0;
            if (rewards.value.users && rewards.value.users.length === 1) {
                program.value.user = rewards.value.users[0];
            }
        }
    }, { immediate: true });

    watch(user, (newUser) => {
        if (newUser && newUser.id) {
            program.value.user = rewards.value.users.find((u) => u.id === newUser.id) || null;
        }
    });

    // Init
    if (rewards.value.users && rewards.value.users.length === 1) {
        program.value.user = rewards.value.users[0];
    }
    if (user.value && user.value.id) {
        program.value.user = rewards.value.users.find((u) => u.id === user.value.id) || null;
    }

    return {
        // State
        step,
        errors,
        saveProgramBtnAction,
        programSearchQuery,
        showUserDropdown,
        dropdownTop,
        dropdownLeft,
        dropdownWidth,
        tooltipVisible,
        tooltipStyle,
        updateMethod,
        program,
        programTypes,

        // Store refs
        programs,
        rewards,
        showProgramModal,

        // Computed
        cdnUrl,
        filteredProgramTypes,
        chosenProgramType,
        chosenProgram,
        userPrograms,
        programChoices,
        canChooseProgramChoice,
        filteredProgramChoices,
        milesPoints,
        canSaveStep,
        canNextStep,
        totalSteps,
        stepSubtitle,
        formattedPoints,

        // Methods
        getProgramImageUrl,
        resetProgram,
        nextStep,
        prevStep,
        addReward,
        addAnotherReward,
        closeModal,
        toggleUserDropdown,
        selectUser,
        closeUserDropdown,
        clearError,
        showTooltip,
        hideTooltip,
    };
}
